// src/hooks/useLiveOutgoing.js
// Live status for the Outgoing Faxes list. Polls the backend for rows that
// can still change and merges the results into the list in place:
//   - queued faxes (until delivered or failed)
//   - recently failed faxes, until we know whether an automatic retry is
//     scheduled; those with a retry keep being watched until it is sent
// When a fax is retried or resent, the new attempt is added to the top of
// the list (if it matches the filters in use) and is then watched itself.
// Polling runs every 5 s while a fax is in progress (15 s otherwise) and
// pauses while the browser tab is hidden, resuming on return.
import { useEffect, useRef, useState } from 'react';
import { fetchOutgoingStatus } from '../utils/api';

export const POLL_MS        = 15 * 1000;       // failed faxes waiting for a retry
export const FAST_POLL_MS   = 5 * 1000;        // while any fax is still in progress
const RECENT_FAILURE_MS     = 60 * 60 * 1000;   // retries fire at +10 and +30 min
const MAX_IDS               = 100;

function isWatchable(row, settled, now) {
  if (row.requeued) return false;
  if (row.status === 'queued') return true;
  if (row.status !== 'failed' || settled.has(row.faxId)) return false;
  if (row.retryAt) return true;
  const t = Date.parse(row.updatedAt || row.sentAt || 0);
  return now - t < RECENT_FAILURE_MS;
}

// Does a new attempt belong in the list under the filters last searched?
function matchesFilters(fax, f) {
  if (!f) return true;
  if (f.status && f.status !== fax.status) return false;
  if (f.startDate && (fax.sentAt || '') < f.startDate) return false;
  if (f.endDate && (fax.sentAt || '') > f.endDate) return false;
  return true;
}

export function useLiveOutgoing({ enabled, data, setData, appliedFilters, getToken, onEvents }) {
  const settledRef  = useRef(new Set());   // failed faxes known to have no retry coming
  const dataRef     = useRef(data);
  const filtersRef  = useRef(appliedFilters);
  const eventsRef   = useRef(onEvents);
  const inFlightRef = useRef(false);
  const tokenRef    = useRef(getToken);
  const lastRef     = useRef(0);
  tokenRef.current  = getToken;
  const [lastChecked, setLastChecked] = useState(null);

  dataRef.current    = data;
  filtersRef.current = appliedFilters;
  eventsRef.current  = onEvents;

  // A fresh search starts a fresh watch
  useEffect(() => { settledRef.current = new Set(); }, [appliedFilters]);

  const now       = Date.now();
  const watchIds  = enabled
    ? data.filter(r => isWatchable(r, settledRef.current, now)).map(r => r.faxId).slice(0, MAX_IDS)
    : [];
  const watchKey  = watchIds.join(',');
  // Poll faster while a fax is actually in progress
  const fast      = enabled && data.some(r => r.status === 'queued' && !r.requeued && watchIds.includes(r.faxId));
  const interval  = fast ? FAST_POLL_MS : POLL_MS;
  // Rows never polled yet (retryAt undefined) are checked right away, so
  // a failed fax shows its scheduled retry without waiting a full cycle
  const needsFirstLook = enabled && data.some(r => watchIds.includes(r.faxId) && r.retryAt === undefined);

  useEffect(() => {
    if (!watchKey) return;
    const ids = watchKey.split(',');
    let stopped = false;

    async function tick() {
      if (stopped || document.hidden || inFlightRef.current) return;
      // Returning to the tab or a new watch set never checks more often than every 3 s
      if (Date.now() - lastRef.current < 3000) return;
      inFlightRef.current = true;
      lastRef.current = Date.now();
      try {
        const { statuses = [], newAttempts = [] } = await fetchOutgoingStatus(ids, () => tokenRef.current());
        if (stopped) return;
        applyUpdate(statuses, newAttempts);
        setLastChecked(new Date());
      } catch {
        // Transient network/API errors: keep the list as is, try next tick
      } finally {
        inFlightRef.current = false;
      }
    }

    function applyUpdate(statuses, newAttempts) {
      const before = new Map(dataRef.current.map(r => [r.faxId, r]));
      const events = [];

      for (const s of statuses) {
        const old = before.get(s.faxId);
        if (!old) continue;
        if (s.status === 'failed' && !s.requeued && !s.retryAt) settledRef.current.add(s.faxId);
        if (old.status === s.status) {
          if (!old.retryAt && s.retryAt && s.status === 'failed') {
            events.push({ kind: 'retrying', fax: { ...old, ...s } });
          }
          continue;
        }
        events.push({ kind: s.status, fax: { ...old, ...s } });
      }

      const existing = new Set(before.keys());
      const added = newAttempts
        .filter(a => !existing.has(a.faxId) && matchesFilters(a, filtersRef.current));
      for (const a of added) events.push({ kind: a.autoRetry ? 'autoRetrySent' : 'resent', fax: a });

      const byId = new Map(statuses.map(s => [s.faxId, s]));
      setData(prev => {
        const merged = prev.map(r => (byId.has(r.faxId) ? { ...r, ...byId.get(r.faxId) } : r));
        const have   = new Set(merged.map(r => r.faxId));
        const fresh  = added.filter(a => !have.has(a.faxId));
        // 3.78.0 (owner): one row per document: a try that was sent again gives
        // way to its next try once that is in the list (3.79.1: manual resends too)
        const all = [...fresh, ...merged];
        const ids = new Set(all.map(r => r.faxId));
        const kept = all.filter(r => !(r.requeued && r.requeuedFaxId && ids.has(r.requeuedFaxId)));
        if (!fresh.length && kept.length === merged.length) return merged;
        return kept.sort((a, b) => (b.sentAt || '').localeCompare(a.sentAt || ''));
      });

      if (events.length) eventsRef.current?.(events);
    }

    function onVisible() { if (!document.hidden) tick(); }

    if (needsFirstLook) tick();
    const timer = setInterval(tick, interval);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  // needsFirstLook only matters when the watch set changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchKey, interval, setData]);

  return { watching: watchIds.length, lastChecked, interval };
}
